// StudentIntakeController.jsx
// Main controller for the 4-step student intake flow

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import confetti from 'canvas-confetti';
import Step1AboutYou from './Step1AboutYou';
import Step2Academics from './Step2Academics';
import Step3Preferences from './Step3Preferences';
import Step4Location from './Step4Location';

// FIX 5 — Full US state name → 2-letter code map
const STATE_MAP = {
  Alabama: 'AL', Alaska: 'AK', Arizona: 'AZ', Arkansas: 'AR',
  California: 'CA', Colorado: 'CO', Connecticut: 'CT', Delaware: 'DE',
  Florida: 'FL', Georgia: 'GA', Hawaii: 'HI', Idaho: 'ID',
  Illinois: 'IL', Indiana: 'IN', Iowa: 'IA', Kansas: 'KS',
  Kentucky: 'KY', Louisiana: 'LA', Maine: 'ME', Maryland: 'MD',
  Massachusetts: 'MA', Michigan: 'MI', Minnesota: 'MN', Mississippi: 'MS',
  Missouri: 'MO', Montana: 'MT', Nebraska: 'NE', Nevada: 'NV',
  'New Hampshire': 'NH', 'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY',
  'North Carolina': 'NC', 'North Dakota': 'ND', Ohio: 'OH', Oklahoma: 'OK',
  Oregon: 'OR', Pennsylvania: 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
  'South Dakota': 'SD', Tennessee: 'TN', Texas: 'TX', Utah: 'UT',
  Vermont: 'VT', Virginia: 'VA', Washington: 'WA', 'West Virginia': 'WV',
  Wisconsin: 'WI', Wyoming: 'WY',
};

const StudentIntakeController = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');

  const [formData, setFormData] = useState({
    // Step 1 — About You
    first_name: "",
    last_name: "",
    email: "",
    birthdate: "",
    gender: "",

    // Step 2 — Academics
    student_type: "",
    highSchool: null, // { id, name, city, state, zip, type, lat, lng }
    high_school_name: "",
    high_school_id: null,
    high_school_city: null,
    high_school_state: null,
    high_school_grad_year: "",
    gpa: "",
    sat_score: "",
    act_score: "",
    intended_major: "",
    alternate_major: "",
    rotc: "",
    preferred_learning_mode: "",

    // Step 3 — Preferences
    college_size: "",
    distance_from_home: "",
    saved_colleges: [],

    // Step 4 — Location & Phone
    phone: "",
    address: "",
    city: "",
    state: "",
    postal_code: "",
    first_gen: "",
    ethnicity: "",
    consent: false,

    onboarding_completed: false
  });

  // Populate form with user data when available
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        email: user.email || ""
      }));
    }
  }, [user]);

  // Helper: Update a single field in formData
  const updateField = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  // Validation logic
  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      // Step 1: All required
      if (!formData.first_name.trim()) newErrors.first_name = 'First name is required';
      if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required';
      if (!formData.email.trim()) newErrors.email = 'Email is required';
      if (!formData.birthdate) newErrors.birthdate = 'Date of birth is required';
      if (!formData.gender) newErrors.gender = 'Gender is required';
    }
    
    if (step === 2) {
      // Step 2: Required fields (High School, Student Type, Grad Year, GPA)
      if (!formData.highSchool?.id) newErrors.highSchool = 'High school selection is required';
      if (!formData.student_type) newErrors.student_type = 'Student type is required';
      if (!formData.high_school_grad_year) newErrors.high_school_grad_year = 'Graduation year is required';
      if (!formData.gpa) newErrors.gpa = 'GPA is required';
    }
    
    if (step === 3) {
      // Step 3: Required fields (Education Format, Intended Major)
      if (!formData.preferred_learning_mode) newErrors.preferred_learning_mode = 'Education format is required';
      if (!formData.intended_major || !formData.intended_major.trim()) newErrors.intended_major = 'Intended major is required';
    }
    
    if (step === 4) {
      // Step 4: Required fields
      if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
      if (!formData.postal_code.trim()) newErrors.postal_code = 'Postal code is required';
      if (!formData.consent) newErrors.consent = 'You must agree to continue';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Navigation: Go to next step
  const nextStep = () => {
    if (validateStep(currentStep) && currentStep < 4) {
      setCurrentStep(prev => prev + 1);
      setErrors({});
    }
  };

  // Navigation: Go to previous step
  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  // Submit handler
  const handleSubmit = async () => {
    // Validate step 4 before submitting
    if (!validateStep(currentStep)) {
      return;
    }

    setLoading(true);
    setSubmitError('');

    try {
      // FIX 3 — Normalize first_gen: "Unsure" → null (can't coerce to bool)
      const normalizedFirstGen =
        formData.first_gen === 'Unsure' || formData.first_gen === ''
          ? null
          : formData.first_gen;

      // FIX 4 — Normalize rotc: empty string → null
      const normalizedRotc =
        formData.rotc === '' || formData.rotc === null ? null : formData.rotc;

      // FIX 5 — Normalize state: full name → 2-letter code
      const normalizedState = STATE_MAP[formData.state] || formData.state || null;

      const submitData = {
        ...formData,
        onboarding_completed: true,
        // FIX 1 — Map highSchool compound object to flat DB fields
        high_school_name: formData.highSchool?.name || '',
        high_school_id: formData.highSchool?.id || null,
        high_school_city: formData.highSchool?.city || null,
        high_school_state: formData.highSchool?.state || null,
        // Apply normalizations
        first_gen: normalizedFirstGen,
        rotc: normalizedRotc,
        state: normalizedState,
        // Exclude the compound object — not a ProfileUpdate field
        highSchool: undefined,
      };

      // Call API to save profile
      await api.put('/api/user/profile', submitData);

      // FIX 2 — Save colleges picked during intake
      if (formData.saved_colleges && formData.saved_colleges.length > 0) {
        await Promise.allSettled(
          formData.saved_colleges.map((collegeId) =>
            api.post('/api/users/saved-colleges', { item_id: collegeId, item_type: 'college' })
          )
        );
      }

      // Trigger confetti animation
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });

      // Wait 2 seconds for confetti
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);

    } catch (error) {
      console.error('Profile update failed:', error);
      setSubmitError(error.response?.data?.detail || 'Failed to save profile. Please try again.');
      setLoading(false);
    }
  };

  // Render the appropriate step component
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1AboutYou formData={formData} updateField={updateField} />;
      case 2:
        return <Step2Academics formData={formData} updateField={updateField} errors={errors} />;
      case 3:
        return <Step3Preferences formData={formData} updateField={updateField} />;
      case 4:
        return <Step4Location formData={formData} updateField={updateField} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#004C3F' }}>
      {/* Max-width container for desktop */}
      <div className="min-h-screen mx-auto flex" style={{ maxWidth: '1280px' }}>
        {/* LEFT PANEL - Persistent Green Branding (50% width) */}
        <div 
          className="hidden lg:flex lg:w-1/2 flex-col justify-center p-10"
          style={{ backgroundColor: '#004C3F' }}
        >
          {/* Logo - Top */}
          <div className="flex items-center mb-10">
            <img 
              src="/2026_logo.png" 
              alt="Student Signal" 
              className="h-[52px] w-auto"
              style={{ imageRendering: '-webkit-optimize-contrast', filter: 'brightness(0) invert(1)' }}
            />
          </div>

          {/* Main Content - Vertically centered */}
          <div className="space-y-5">
            <h1 className="text-3xl lg:text-4xl font-bold text-white leading-tight">
              Find colleges and scholarships that actually fit you.
            </h1>
            <p className="text-base text-white/80">
              One profile. Smarter matches. No spam.
            </p>
            
            {/* Time indicator */}
            <div className="flex items-center gap-2 pt-4">
              <span className="text-yellow-400">🎓</span>
              <span className="text-sm text-white/70">Takes less than 5 minutes to complete</span>
            </div>
          </div>
        </div>

        {/* RIGHT PANEL - Form Content (50% width) */}
        <div 
          className="w-full lg:w-1/2 min-h-screen flex flex-col"
          style={{ backgroundColor: '#F9FAFB' }}
        >
          {/* Mobile Logo - Only visible on small screens */}
          <div className="lg:hidden p-6 flex justify-center" style={{ backgroundColor: '#004C3F' }}>
            <img 
              src="/2026_logo.png" 
              alt="Student Signal" 
              className="h-[44px] w-auto"
              style={{ imageRendering: '-webkit-optimize-contrast', filter: 'brightness(0) invert(1)' }}
            />
          </div>

          {/* Form Container */}
          <div className="flex-1 flex items-center justify-center p-6 lg:p-10">
            <div 
              className="bg-white w-full"
              style={{ 
                maxWidth: '440px', 
                padding: '36px',
                borderRadius: '16px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
              }}
            >
              {/* Error Message */}
              {submitError && (
                <div 
                  className="mb-6 p-4 text-sm text-center"
                  style={{ 
                    backgroundColor: '#FEF2F2', 
                    color: '#D92D20', 
                    border: '1px solid #FECACA', 
                    borderRadius: '12px' 
                  }}
                >
                  {submitError}
                </div>
              )}

              {/* Step Content */}
              {renderStep()}

              {/* Navigation Buttons */}
              <div 
                className="flex items-center justify-between mt-8 pt-6" 
                style={{ borderTop: '1px solid #E1E4E8' }}
              >
                {/* Back Button */}
                {currentStep > 1 && (
                  <button
                    onClick={prevStep}
                    className="px-6 py-3 font-medium text-sm transition-all hover:bg-gray-50"
                    style={{
                      color: '#2A2F35',
                      backgroundColor: 'transparent',
                      border: '1px solid #E1E4E8',
                      borderRadius: '8px'
                    }}
                  >
                    Back
                  </button>
                )}

                {/* Spacer for first step */}
                {currentStep === 1 && <div></div>}

                {/* Continue or Submit Button */}
                {currentStep < 4 ? (
                  <button
                    onClick={nextStep}
                    className="px-6 py-3 font-medium text-sm flex items-center gap-2 transition-all hover:opacity-90"
                    style={{
                      backgroundColor: '#004C3F',
                      color: 'white',
                      height: '48px',
                      borderRadius: '8px'
                    }}
                  >
                    Continue
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                ) : (
                  <button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="px-6 py-3 font-medium text-sm flex items-center gap-2 transition-all disabled:opacity-50 hover:opacity-90"
                    style={{
                      backgroundColor: '#004C3F',
                      color: 'white',
                      height: '48px',
                      borderRadius: '8px'
                    }}
                  >
                    {loading ? 'Completing...' : 'Complete Profile'}
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentIntakeController;
